import { format, isSameMonth, startOfDay } from 'date-fns'
import type { IUserMonthDays, IUserPositionDay } from '@/api/user/getUserMonthAvailability.ts'

export type DayAvailability = 'located' | 'unavailable-only' | 'none'

export type CalendarCellState = 'outside' | 'future' | 'selected' | 'loading' | DayAvailability

export interface ICalendarCellInput {
    date: Date
    currentMonth: Date
    today: Date
    selectedDate: string
    isLoading: boolean
    day?: IUserPositionDay
}

export interface ICalendarCell {
    state: CalendarCellState
    selectable: boolean
}

export const PAST_MONTH_STALE_MS = 30 * 60 * 1000
export const CURRENT_MONTH_STALE_MS = 30 * 1000

const monthIndex = (year: number, month: number): number => year * 12 + month

const currentMonthIndex = (now: Date): number => monthIndex(now.getFullYear(), now.getMonth() + 1)

export const dayAvailability = (day?: IUserPositionDay): DayAvailability => {
    if (!day) return 'none'
    if (day.located > 0) return 'located'
    return day.unavailable > 0 ? 'unavailable-only' : 'none'
}

export const buildAvailabilityMap = (days: IUserPositionDay[]): Map<string, IUserPositionDay> =>
    new Map(days.map(day => [day.date, day]))

export const isFutureMonth = (year: number, month: number, now: Date = new Date()): boolean =>
    monthIndex(year, month) > currentMonthIndex(now)

// 지난 달은 기록이 더 쌓이지 않아 오래 두고, 이번 달만 자주 다시 받는다.
export const availabilityStaleTime = (year: number, month: number, now: Date = new Date()): number =>
    monthIndex(year, month) < currentMonthIndex(now) ? PAST_MONTH_STALE_MS : CURRENT_MONTH_STALE_MS

export interface IMonthRef {
    year: number
    month: number
}

export interface ICalendarView {
    month: IMonthRef
    days: IUserPositionDay[]
}

const NO_DAYS: IUserPositionDay[] = []

// 달력이 보여줄 달과 기록. 다른 달로 넘기면 그 달의 기록이 올 때까지 직전에 받은 달을 그대로 보여 주고,
// 오면 한 번에 바꾼다. 아직 오지 않은 달은 받을 기록이 없어 바로 보여준다.
export const resolveCalendarView = (
    target: IMonthRef,
    data: IUserMonthDays | undefined,
    now: Date = new Date()
): ICalendarView => {
    if (isFutureMonth(target.year, target.month, now) || !data) return { month: target, days: NO_DAYS }

    return { month: { year: data.year, month: data.month }, days: data.days }
}

export const calendarCellState = ({
    date,
    currentMonth,
    today,
    selectedDate,
    isLoading,
    day
}: ICalendarCellInput): ICalendarCell => {
    if (!isSameMonth(date, currentMonth)) return { state: 'outside', selectable: false }
    if (startOfDay(date).getTime() > today.getTime()) return { state: 'future', selectable: false }

    const availability = dayAvailability(day)
    const selectable = !isLoading && availability !== 'none'
    if (format(date, 'yyyy-MM-dd') === selectedDate) return { state: 'selected', selectable }
    if (isLoading) return { state: 'loading', selectable: false }

    return { state: availability, selectable }
}
